/**
 * "How National can help", starting from the controls (Andres, 2026-09-24): what each National service is
 * good for and who can use it (the guidance page), and on a firm's dashboard its own open gaps matched to
 * the services that would help close them. The firm decides what to adopt.
 * Content: packages/shared/src/national-services.ts (draft until signed off).
 */
import {
  AVAILABILITY,
  CONTROL_SUMMARIES,
  controlsForService,
  COVERAGE_LEVEL,
  isHandsOn,
  NATIONAL_CATALOGUE,
  servicesForControl,
  strongestLevel,
  type CoverageLevel,
  type NationalOffering,
} from '@grc/shared/vocabulary';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, useApi, useApiClient } from '../api';
import type { FirmControls } from '../types';
import { PlanProposal } from './PlanProposal';

const byControl = (a: string, b: string) => (a.startsWith('E8') ? 1 : 0) - (b.startsWith('E8') ? 1 : 0) || a.localeCompare(b, 'en', { numeric: true });
const ALL_CONTROLS = Object.keys(CONTROL_SUMMARIES).sort(byControl);
const BTC = ALL_CONTROLS.filter((c) => !c.startsWith('E8'));
const cid = (c: string) => (c.startsWith('E8') ? 'Essential Eight' : `CIS ${c}`);
/** Gaps listed per service before "Show all", so the page stays short on a phone. */
const SHOWN_PER_SERVICE = 4;

/** A small mark for a coverage level; the shape carries the meaning, not only the colour. */
function Mark({ level }: { level: CoverageLevel }) {
  return <span className={`cov-mark cov-${level}`} aria-hidden="true" />;
}

/** One-line headline for the page. */
export function CoverageSummary() {
  const handsOn = BTC.filter((c) => isHandsOn(strongestLevel(c))).length;
  const guidance = BTC.filter((c) => strongestLevel(c) === 'guidance').length;
  return (
    <p className="coverage-summary">
      <span className="coverage-big">{handsOn}</span>
      <span>
        of the {BTC.length} BTC Baseline controls get hands-on help from a National service
        {guidance > 0 && (guidance === BTC.length - handsOn ? <>; National guidance covers the other {guidance}</> : <>; National guidance covers {guidance} more</>)}.
      </span>
    </p>
  );
}

/** The request an IT lead starts from: the service and a plain question, which they can change before sending. */
export const detailsQuestion = (s: NationalOffering) =>
  `Could you send us the details for ${s.name}: the network price, what National IT sets up, what our firm would run, and how we sign up?`;

/** What the request records as its context, so National IT sees it came from a service tile. */
export const detailsContext = (s: NationalOffering) => `Request details: ${s.name}`.slice(0, 200);

/**
 * "Request details" on an opt-in service (DEC-49): a card with the question filled in for the IT lead to edit and send.
 * It goes through the existing request route (POST /firms/{firm}/national-requests, firm IT lead only) and shows in
 * National IT's "Requests from firms" and under the firm's own "Your requests".
 */
function RequestDetails({ service, firm, onDone, onSent }: { service: NationalOffering; firm: { firmCode: string; displayName: string }; onDone: () => void; onSent: () => void }) {
  const call = useApiClient();
  const [question, setQuestion] = useState(() => detailsQuestion(service));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await call(`/firms/${firm.firmCode}/national-requests`, { method: 'POST', body: { question: question.trim(), summary: null, context: detailsContext(service) } });
      onSent();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The request was not sent. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="request-details" aria-label={`Request details about ${service.name}`} onSubmit={send}>
      <h4>Request details about {service.name}</h4>
      <label>
        Your request
        {/* Opening the form puts the cursor in the request (UX-04). */}
        <textarea rows={4} maxLength={4000} value={question} onChange={(e) => setQuestion(e.target.value)} disabled={busy} autoFocus />
      </label>
      <p className="small muted">It goes to National IT for {firm.displayName}, with your name. You can change the wording first.</p>
      <div className="actions">
        <button className="pill" disabled={busy || question.trim().length < 10}>
          {busy ? 'Sending…' : 'Send to National IT'}
        </button>
        <button type="button" className="pill secondary-dark" onClick={onDone} disabled={busy}>
          Not now
        </button>
      </div>
      {error && (
        <p role="alert" className="notice">
          {error}
        </p>
      )}
    </form>
  );
}

/**
 * Each service: what it is good for, who can use it, and how many controls it helps with. Opt-in services offer
 * "Request details" to the firm's IT lead (requestFirm); a firm member who is not the IT lead is told who can ask.
 */
export function ServiceCards({ requestFirm, memberOnly = false, onRequested, askHref }: {
  requestFirm?: { firmCode: string; displayName: string };
  memberOnly?: boolean;
  onRequested?: () => void;
  askHref?: string;
} = {}) {
  const [open, setOpen] = useState<string | null>(null);
  const [requesting, setRequesting] = useState<string | null>(null);
  const [sent, setSent] = useState<string[]>([]);
  // Where focus goes once the form closes (UX-04): back to its button on "Not now", to the "Sent" line on send.
  const focusNext = useRef<string | null>(null);
  const setFocusNext = (id: string) => {
    focusNext.current = id;
  };
  useEffect(() => {
    if (!focusNext.current) return;
    document.getElementById(focusNext.current)?.focus();
    focusNext.current = null;
  });
  const card = (s: NationalOffering) => {
    const rows = controlsForService(s.id);
    const handsOn = rows.filter((r) => isHandsOn(r.level));
    return (
      <li key={s.id} className={`service-card avail-${s.availability}`}>
        <div className="service-head">
          <h3>{s.name}</h3>
          <span className={`avail-chip avail-${s.availability}`}>{AVAILABILITY[s.availability].label}</span>
        </div>
        <p className="service-line">{s.line}</p>
        {s.capabilities.length > 0 && (
          <ul className="service-caps small">
            {s.capabilities.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        )}
        {s.availability === 'opt_in' && requestFirm && (
          <div className="service-request">
            {sent.includes(s.id) ? (
              <p role="status" className="ok small" id={`request-sent-${s.id}`} tabIndex={-1}>
                Sent to National IT. The answer appears under {askHref ? <Link to={askHref}>Your requests</Link> : 'Your requests'} on the Ask National tab.
              </p>
            ) : requesting === s.id ? (
              <RequestDetails
                service={s}
                firm={requestFirm}
                onDone={() => {
                  setRequesting(null);
                  setFocusNext(`request-btn-${s.id}`);
                }}
                onSent={() => {
                  setRequesting(null);
                  setSent((x) => [...x, s.id]);
                  setFocusNext(`request-sent-${s.id}`);
                  onRequested?.();
                }}
              />
            ) : (
              <button type="button" id={`request-btn-${s.id}`} className="pill secondary-dark" onClick={() => setRequesting(s.id)}>
                Request details<span className="sr-only"> about {s.name}</span>
              </button>
            )}
          </div>
        )}
        {s.availability === 'opt_in' && !requestFirm && memberOnly && <p className="muted small service-request">Your firm's IT lead can ask National IT for details.</p>}
        {rows.length > 0 && (
          <>
            <p className="small service-count">
              {s.availability === 'evaluating' ? (
                <>Would help with {rows.length} controls once available</>
              ) : handsOn.length ? (
                <>
                  <strong>{handsOn.length}</strong> {handsOn.length === 1 ? 'control' : 'controls'} with hands-on help
                  {rows.length > handsOn.length && <>, guidance for {rows.length - handsOn.length} more</>}
                </>
              ) : (
                <>
                  Guidance for <strong>{rows.length}</strong> {rows.length === 1 ? 'control' : 'controls'}
                </>
              )}
            </p>
            <button type="button" className="link service-toggle" aria-expanded={open === s.id} onClick={() => setOpen(open === s.id ? null : s.id)}>
              {open === s.id ? 'Hide the controls' : 'Show the controls'}
              <span className="sr-only"> for {s.name}</span>
            </button>
            {open === s.id && (
              <ul className="plain service-controls small">
                {[...rows].sort((a, b) => byControl(a.controlId, b.controlId)).map((r) => (
                  <li key={r.controlId}>
                    <Mark level={r.level} />
                    <span>
                      <Link to={`/guidance/controls/${encodeURIComponent(r.controlId)}`}>{CONTROL_SUMMARIES[r.controlId] ?? r.controlId}</Link>{' '}
                      <span className="muted">
                        {cid(r.controlId)}, {COVERAGE_LEVEL[r.level].label.toLowerCase()}. {r.how}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </li>
    );
  };
  return (
    <>
      <h2 className="coverage-h">Platforms</h2>
      <ul className="service-cards" aria-label="National platforms">
        {NATIONAL_CATALOGUE.filter((s) => s.kind === 'platform').map(card)}
      </ul>
      <h2 className="coverage-h">Frameworks and guidance</h2>
      <ul className="service-cards" aria-label="National frameworks">
        {NATIONAL_CATALOGUE.filter((s) => s.kind === 'framework').map(card)}
      </ul>
    </>
  );
}

/**
 * The firm's own open gaps (current status: not yet or partly in place), grouped by the service that would
 * help most, with a way to ask National or track it as an action plan.
 */
export function FirmGapsNationalCanHelp({ firm }: { firm: { firmCode: string; displayName: string; canWrite: boolean } }) {
  const state = useApi<FirmControls>(`/firms/${firm.firmCode}/controls`);
  const [tracking, setTracking] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string[]>([]);
  if (state.loading) return <p className="muted">Matching your open gaps to National services…</p>;
  if (state.error || !state.data) return null;
  const gaps = state.data.controls.filter((c) => c.currentStatus === 'gap' || c.currentStatus === 'partial_gap').map((c) => c.controlId);
  if (!gaps.length) return null;
  const groups = NATIONAL_CATALOGUE.filter((s) => s.availability !== 'evaluating')
    .map((s) => ({ s, rows: controlsForService(s.id).filter((r) => isHandsOn(r.level) && gaps.includes(r.controlId)) }))
    .filter((g) => g.rows.length)
    .sort((a, b) => b.rows.length - a.rows.length);
  const helped = new Set(groups.flatMap((g) => g.rows.map((r) => r.controlId)));
  const guidanceOnly = gaps.filter((c) => !helped.has(c) && servicesForControl(c).some((r) => r.level === 'guidance'));
  return (
    <section className="card firm-gaps-card" aria-labelledby="fgaps-h">
      <h2 id="fgaps-h">Gaps National can help close</h2>
      <p className="coverage-summary">
        <span className="coverage-big">{helped.size}</span>
        <span>
          of your {gaps.length} open gaps have hands-on help from a National service{guidanceOnly.length > 0 && <>; National guidance covers {guidanceOnly.length} more</>}.
        </span>
      </p>
      <details className="firm-gaps-details">
        <summary>See which National services help with which gaps</summary>
      <ul className="plain firm-gap-groups">
        {groups.map(({ s, rows }) => {
          const all = expanded.includes(s.id);
          const shown = [...rows].sort((a, b) => byControl(a.controlId, b.controlId)).slice(0, all ? undefined : SHOWN_PER_SERVICE);
          const names = [...new Set(rows.map((r) => (r.controlId.startsWith('E8') ? 'Essential Eight' : r.controlId)))];
          const question = `How do we get started with ${s.name}, and what would it take to use it for controls ${names.slice(0, 5).join(', ')}${names.length > 5 ? ` and ${names.length - 5} more` : ''}?`;
          return (
          <li key={s.id} className="firm-gap-group">
            <div className="service-head">
              <h3>{s.name}</h3>
              <span className={`avail-chip avail-${s.availability}`}>{AVAILABILITY[s.availability].label}</span>
              <span className="muted small">
                helps with {rows.length} of your open {rows.length === 1 ? 'gap' : 'gaps'}
              </span>
            </div>
            <ul className="plain small firm-gap-list">
              {shown.map((r) => {
                const key = `${s.id}:${r.controlId}`;
                return (
                  <li key={r.controlId}>
                    <div className="firm-gap-row">
                      <span>
                        <Mark level={r.level} /> <Link to={`/firm/${firm.firmCode}/controls/${encodeURIComponent(r.controlId)}`}>{CONTROL_SUMMARIES[r.controlId]}</Link>{' '}
                        <span className="muted">{r.how}</span>
                      </span>
                      {firm.canWrite && tracking !== key && (
                        <button type="button" className="link" onClick={() => setTracking(key)}>
                          Track as an action plan<span className="sr-only">: {CONTROL_SUMMARIES[r.controlId]} with {s.name}</span>
                        </button>
                      )}
                    </div>
                    {tracking === key && (
                      <PlanProposal
                        firmCode={firm.firmCode}
                        canAdd={firm.canWrite}
                        onDone={() => setTracking(null)}
                        plan={{
                          theme: `Adopt ${s.name}`,
                          objective: `Use ${s.name} to put this in place: ${CONTROL_SUMMARIES[r.controlId]}`.slice(0, 600),
                          controls: [r.controlId],
                          priority: 'medium',
                          why: `${s.name}: ${r.how}`,
                        }}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="actions">
              {rows.length > SHOWN_PER_SERVICE && (
                <button type="button" className="link" aria-expanded={all} onClick={() => setExpanded(all ? expanded.filter((x) => x !== s.id) : [...expanded, s.id])}>
                  {all ? 'Show fewer' : `Show all ${rows.length}`}
                  <span className="sr-only"> for {s.name}</span>
                </button>
              )}
              <Link className="pill secondary-dark ask-about" to={`/guidance/resources?firm=${encodeURIComponent(firm.firmCode)}&ask=${encodeURIComponent(question)}#ask`}>
                Ask National about {s.name}
              </Link>
            </div>
          </li>
          );
        })}
      </ul>
      </details>
      <p className="small">
        <Link to={`/guidance/resources?firm=${encodeURIComponent(firm.firmCode)}`}>All National services, and who can use them</Link>
      </p>
    </section>
  );
}
